import React from 'react';
import { motion } from 'framer-motion';
import {
  FaBullseye,
  FaEye,
  FaHandshake,
  FaStar,
  FaTrophy,
  FaUsers,
  FaDumbbell,
  FaFire,
  FaQuoteLeft,
  FaArrowRight,
  FaMapMarkerAlt,
  FaChartLine,
  FaLayerGroup,
  FaCheckCircle
} from 'react-icons/fa';
import NumberCounter from 'number-counter';
import { Link } from 'react-router-dom';
import image1 from '../assets/image1.png';
import image2 from '../assets/image2.png';
import image3 from '../assets/image3.png';
import image4 from '../assets/image4.png';
import tick from '../assets/tick.png';
import { branchesData } from '../data/branchesData';

const AboutUs = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  };
  const fadeLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
  };
  const fadeRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
  };
  const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } },
  };

  const stats = [
    { end: 3, start: 1, label: 'Units in Coimbatore', icon: <FaMapMarkerAlt /> },
    { end: 140, start: 69, label: 'Expert Coaches', icon: <FaTrophy /> },
    { end: 978, start: 800, label: 'Happy Members', icon: <FaUsers /> },
    { end: 8, start: 1, label: 'Years of Excellence', icon: <FaFire /> },
  ];

  const values = [
    {
      icon: <FaStar className="text-2xl" />,
      title: 'Excellence',
      description:
        'We maintain the highest standards in equipment, coaching, and facility management across all 3 units.',
      color: '#e51b24',
    },
    {
      icon: <FaHandshake className="text-2xl" />,
      title: 'Community',
      description:
        'Fitness is better together. We foster a supportive, inclusive environment where every member belongs.',
      color: '#ef233c',
    },
    {
      icon: <FaFire className="text-2xl" />,
      title: 'Dedication',
      description:
        'From 5 AM to 10 PM, our coaches and staff are fully committed to your transformation journey.',
      color: '#3498db',
    },
    {
      icon: <FaBullseye className="text-2xl" />,
      title: 'Results-Driven',
      description:
        'Every programme, meal plan, and session is designed with one goal — measurable, lasting results.',
      color: '#27ae60',
    },
  ];

  const trainingApproachSteps = [
    {
      step: '01',
      title: 'Assess',
      description: 'Understand the client’s goal, experience, movement ability, schedule and current fitness level.',
      icon: <FaBullseye className="text-red-500 text-xl" />
    },
    {
      step: '02',
      title: 'Plan',
      description: 'Select exercises, training frequency, volume, intensity and progression around the individual.',
      icon: <FaLayerGroup className="text-red-500 text-xl" />
    },
    {
      step: '03',
      title: 'Train',
      description: 'Coach technique, execution, effort and consistency.',
      icon: <FaDumbbell className="text-red-500 text-xl" />
    },
    {
      step: '04',
      title: 'Track',
      description: 'Monitor training performance, measurements and relevant progress markers.',
      icon: <FaChartLine className="text-red-500 text-xl" />
    },
    {
      step: '05',
      title: 'Adapt',
      description: 'Modify the plan according to progress, recovery and changing goals.',
      icon: <FaCheckCircle className="text-red-500 text-xl" />
    },
    {
      step: '06',
      title: 'Progress',
      description: 'Gradually increase training demands while maintaining quality and sustainability.',
      icon: <FaFire className="text-red-500 text-xl" />
    },
  ];

  const keyPrinciples = [
    'Science-backed programming',
    'Progressive overload',
    'Correct technique',
    'Mobility',
    'Recovery',
    'Measurable progress'
  ];

  return (
    <div className="flex flex-col w-full overflow-x-hidden pt-12">

      {/* ── SECTION 1: ABOUT HERO ── */}
      <section className="relative text-center py-20 md:py-28 px-4 overflow-hidden" id="about-hero">
        {/* Decorative Blurs */}
        <div className="absolute w-96 h-96 bg-red-600/25 blur-[180px] rounded-full top-0 left-1/2 -translate-x-1/2 -z-10" />
        <div className="absolute w-72 h-72 bg-red-800/20 blur-[140px] rounded-full bottom-0 right-0 -z-10" />

        <motion.div
          className="max-w-4xl mx-auto"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.span
            className="inline-block bg-red-600/20 text-red-500 border border-red-600/30 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
            variants={fadeUp}
          >
            Est. 2016 • 3 Branches in Coimbatore
          </motion.span>
          <motion.h1
            className="text-3xl sm:text-5xl md:text-7xl font-black uppercase tracking-wide mb-6 leading-tight"
            variants={fadeUp}
          >
            <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">About </span>
            <span className="text-red-500">IDOL FITNESS ZONE</span>
          </motion.h1>
          <motion.p
            className="text-gray-300 text-sm sm:text-lg leading-relaxed max-w-2xl mx-auto mb-10"
            variants={fadeUp}
          >
            More than a gym — we're a movement. For over 8 years, IDOL FITNESS ZONE has been Coimbatore's
            premier fitness destination across Chettipalayam JJ Nagar, Pappampatti Pirivu, and Peedampalli.
          </motion.p>

          {/* Hero Stats */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto"
            variants={fadeUp}
          >
            {stats.map((stat, i) => (
              <div
                key={i}
                className="flex flex-col items-center justify-center p-5 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 text-center hover:border-red-500/40 transition-colors duration-300"
              >
                <span className="text-red-500 text-2xl mb-2">{stat.icon}</span>
                <span className="text-2xl md:text-3xl font-extrabold text-white">
                  <NumberCounter end={stat.end} start={stat.start} delay="2" preFix="+" />
                </span>
                <span className="text-gray-400 text-[10px] md:text-xs font-medium uppercase tracking-wider mt-1">
                  {stat.label}
                </span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ── SECTION 2: OUR STORY ── */}
      <section className="relative px-4 md:px-12 py-20 md:py-28 text-white overflow-hidden" id="our-story">
        <div className="absolute w-[30rem] h-[30rem] bg-red-600/15 blur-[200px] rounded-full -top-20 -left-20 -z-10" />

        <div className="max-w-7xl mx-auto">
          {/* Section Label */}
          <motion.div
            className="flex items-center gap-4 mb-12"
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <div className="h-px flex-1 bg-gradient-to-r from-red-600/60 to-transparent" />
            <span className="text-red-500 text-xs font-bold uppercase tracking-[0.3em] flex-shrink-0">Our Journey & Philosophy</span>
            <div className="h-px flex-1 bg-gradient-to-l from-red-600/60 to-transparent" />
          </motion.div>

          {/* Main Content */}
          <div className="relative flex flex-col gap-8">
            <motion.div
              className="relative rounded-3xl overflow-hidden shadow-2xl"
              initial="hidden"
              whileInView="visible"
              variants={fadeUp}
              viewport={{ once: true }}
            >
              <img
                src={image1}
                alt="IDOL FITNESS ZONE Floor"
                className="w-full h-[22rem] sm:h-[28rem] md:h-[32rem] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121316] via-[#121316]/70 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#121316]/80 via-transparent to-transparent" />

              <motion.div
                className="absolute top-6 right-6 md:top-10 md:right-10 bg-red-600 text-white w-20 h-20 md:w-24 md:h-24 rounded-2xl flex flex-col items-center justify-center shadow-xl shadow-red-600/30 border border-white/20"
                initial={{ scale: 0, rotate: -20 }}
                whileInView={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <span className="text-[10px] md:text-xs uppercase font-semibold opacity-80">Since</span>
                <span className="text-2xl md:text-3xl font-black leading-none">2016</span>
              </motion.div>
            </motion.div>

            {/* Story Card + Side Images side by side on md+ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Story Card */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={fadeLeft}
                viewport={{ once: true }}
              >
                <div className="bg-[#181a20]/95 backdrop-blur-2xl p-6 sm:p-8 rounded-3xl border border-white/10 shadow-2xl h-full">
                  <h2 className="text-2xl sm:text-3xl font-black uppercase leading-tight mb-5">
                    Fitness Is Not A Goal,
                    <br />
                    <span className="text-transparent [-webkit-text-stroke:_1px_white]">It's A Lifestyle</span>
                  </h2>
                  <p className="text-gray-300 leading-relaxed text-sm sm:text-base mb-4">
                    From our first branch in Chettipalayam JJ Nagar to our multi-facility network including Pappampatti Pirivu and Peedampalli, IDOL FITNESS ZONE was built on one principle: making world-class fitness accessible to everyone.
                  </p>
                  <p className="text-gray-400 leading-relaxed text-xs sm:text-sm mb-6">
                    With certified coaches like Sanjay and Karthikeyan, Olympic-grade machinery, and customized diet guidance, our members achieve real, lasting transformations.
                  </p>

                  <div className="flex flex-col gap-3">
                    {[
                      { label: 'Member Satisfaction Rate', value: 98 },
                      { label: 'Equipment Quality & Safety', value: 96 },
                      { label: 'Trainer Coaching Standards', value: 95 },
                    ].map((bar, i) => (
                      <div key={i}>
                        <div className="flex justify-between mb-1.5">
                          <span className="text-xs font-semibold uppercase tracking-wider text-gray-300">{bar.label}</span>
                          <span className="text-xs font-bold text-red-400">{bar.value}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full bg-gradient-to-r from-red-600 to-rose-500"
                            initial={{ width: 0 }}
                            whileInView={{ width: `${bar.value}%` }}
                            transition={{ duration: 1.2, delay: 0.3 + i * 0.15, ease: 'easeOut' }}
                            viewport={{ once: true }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Side Image Stack */}
              <motion.div
                className="flex flex-col gap-4"
                initial="hidden"
                whileInView="visible"
                variants={fadeRight}
                viewport={{ once: true }}
              >
                <div className="grid grid-cols-2 gap-4">
                  <img
                    src={image2}
                    alt="Training session"
                    className="w-full h-40 lg:h-48 object-cover rounded-2xl shadow-xl border border-white/10 hover:scale-[1.03] transition-transform duration-300"
                  />
                  <img
                    src={image3}
                    alt="Fitness class"
                    className="w-full h-40 lg:h-48 object-cover rounded-2xl shadow-xl border border-white/10 hover:scale-[1.03] transition-transform duration-300"
                  />
                </div>
                <img
                  src={image4}
                  alt="Gym equipment zone"
                  className="w-full h-32 lg:h-40 object-cover rounded-2xl shadow-xl border border-white/10 hover:scale-[1.03] transition-transform duration-300"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION: TRAINING APPROACH ── */}
      <section className="relative px-4 md:px-12 py-20 bg-gradient-to-b from-transparent via-[#141620]/60 to-transparent overflow-hidden" id="training-approach">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16 flex flex-col items-center gap-3"
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 bg-red-600/20 text-red-500 border border-red-600/30 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest">
              <FaFire /> 6-Step Methodical Framework
            </span>
            <h2 className="text-3xl sm:text-5xl font-black uppercase text-white tracking-wide">
              TRAINING <span className="text-red-500">APPROACH</span>
            </h2>
            <p className="text-gray-300 text-xs sm:text-sm max-w-2xl leading-relaxed">
              Our systematic approach ensures every client receives individualized programming, correct execution coaching, and measurable progress tracking.
            </p>
          </motion.div>

          {/* 6 Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {trainingApproachSteps.map((item) => (
              <motion.div
                key={item.step}
                className="bg-gradient-to-b from-[#191b26] to-[#12141c] p-6 rounded-3xl border border-white/10 hover:border-red-500/50 transition-all duration-300 shadow-xl group relative overflow-hidden flex flex-col justify-between"
                initial="hidden"
                whileInView="visible"
                variants={fadeUp}
                viewport={{ once: true }}
                whileHover={{ y: -6 }}
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent group-hover:via-red-400 transition-all" />

                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-2xl bg-red-600/20 border border-red-500/30 flex items-center justify-center text-red-500 group-hover:bg-red-600 group-hover:text-white transition-all shadow-md">
                      {item.icon}
                    </div>
                    <span className="text-2xl font-black text-white/20 group-hover:text-red-500/40 transition-colors">
                      {item.step}
                    </span>
                  </div>

                  <h3 className="text-xl font-black uppercase text-white group-hover:text-red-400 transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-xs text-gray-300 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Key Principles Banner */}
          <motion.div
            className="bg-gradient-to-r from-red-600/20 via-[#181a24] to-red-600/20 p-6 md:p-8 rounded-3xl border border-red-500/30 backdrop-blur-xl flex flex-col gap-4 shadow-2xl"
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-white/10 pb-4">
              <span className="text-xs font-black uppercase tracking-widest text-red-400 flex items-center gap-2">
                <FaCheckCircle className="text-red-500" /> Core Methodology
              </span>
              <span className="text-sm font-black text-white uppercase tracking-wider">
                Key Principles of IDOL Fitness
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {keyPrinciples.map((principle, pIdx) => (
                <div
                  key={pIdx}
                  className="bg-white/5 border border-white/10 px-3 py-2.5 rounded-xl text-center text-xs font-bold text-gray-200 hover:text-white hover:border-red-500/40 transition-all flex items-center justify-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  <span>{principle}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 3: 3 UNITS OVERVIEW ── */}
      <section className="px-4 md:px-12 py-16 max-w-7xl mx-auto w-full">
        <div className="text-center mb-12">
          <span className="text-red-500 text-xs font-bold uppercase tracking-widest block mb-2">3 Convenient Locations</span>
          <h3 className="text-3xl sm:text-5xl font-black uppercase text-white">Our Coimbatore Branches</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {branchesData.map((branch) => (
            <div key={branch.id} className="bg-white/5 p-6 rounded-3xl border border-white/10 hover:border-red-500/40 transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="bg-red-600 text-white text-xs font-bold uppercase px-3 py-1 rounded-full">{branch.unitNumber}</span>
                  <span className="text-xs text-red-400 font-semibold">{branch.badge}</span>
                </div>
                <h4 className="text-xl font-bold text-white mb-2">{branch.name}</h4>
                <p className="text-xs text-gray-300 mb-4">{branch.address}</p>
              </div>
              <div className="flex flex-col gap-2 pt-4 border-t border-white/10">
                <a href={branch.mapsUrl} target="_blank" rel="noreferrer" className="bg-red-600 hover:bg-red-500 text-white text-xs font-bold py-2 px-4 rounded-xl text-center transition-all">
                  Open Google Maps
                </a>
                <a href={branch.instagram} target="_blank" rel="noreferrer" className="bg-white/5 hover:bg-white/15 text-gray-200 text-xs py-2 px-4 rounded-xl text-center transition-all">
                  Follow on Instagram
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SECTION 4: VISION & MISSION ── */}
      <section className="relative px-4 md:px-12 py-20 text-white overflow-hidden" id="vision-mission">
        <div className="absolute w-96 h-96 bg-red-600/15 blur-[160px] rounded-full top-1/2 right-0 -translate-y-1/2 -z-10" />

        <motion.div
          className="text-center max-w-3xl mx-auto mb-16"
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          viewport={{ once: true }}
        >
          <span className="text-red-500 text-sm font-bold uppercase tracking-widest block mb-2">
            What Drives Us
          </span>
          <div className="text-3xl sm:text-5xl font-extrabold uppercase tracking-wide mb-4">
            <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">Vision & </span>
            <span>Mission</span>
          </div>
        </motion.div>

        {/* Vision / Mission Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto mb-20">
          <motion.div
            className="group relative bg-[#181a20]/90 backdrop-blur-xl p-8 sm:p-10 rounded-3xl border border-white/10 shadow-2xl hover:border-red-500/40 transition-all duration-300 overflow-hidden"
            initial="hidden"
            whileInView="visible"
            variants={fadeLeft}
            viewport={{ once: true }}
          >
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-red-600 via-rose-500 to-red-600 rounded-t-3xl" />
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-red-600/20 flex items-center justify-center text-red-500 text-2xl shadow-md">
                <FaEye />
              </div>
              <h3 className="text-2xl font-bold uppercase tracking-wide">Our Vision</h3>
            </div>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base mb-6">
              To be recognized as Tamil Nadu's benchmark for transformative fitness — combining scientific training, motivating community culture, and multi-unit convenience.
            </p>
            <div className="flex items-start gap-3 bg-white/5 p-4 rounded-xl border border-white/10">
              <FaQuoteLeft className="text-red-500 text-lg flex-shrink-0 mt-1" />
              <p className="text-xs sm:text-sm text-gray-200 italic leading-relaxed">
                "We envision a community where health and strength are accessible to every individual."
              </p>
            </div>
          </motion.div>

          <motion.div
            className="group relative bg-[#181a20]/90 backdrop-blur-xl p-8 sm:p-10 rounded-3xl border border-white/10 shadow-2xl hover:border-red-500/40 transition-all duration-300 overflow-hidden"
            initial="hidden"
            whileInView="visible"
            variants={fadeRight}
            viewport={{ once: true }}
          >
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-red-600 via-orange-500 to-red-600 rounded-t-3xl" />
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-red-600/20 flex items-center justify-center text-red-400 text-2xl shadow-md">
                <FaBullseye />
              </div>
              <h3 className="text-2xl font-bold uppercase tracking-wide">Our Mission</h3>
            </div>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base mb-6">
              To provide an inclusive, results-driven fitness ecosystem with expert coaching, Olympic-grade machinery, and personalized workout & diet protocols.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                'Expert-led coaching for all levels',
                'Science-backed nutrition guidance',
                'Welcoming 3-unit network',
                'Continuous facility upgrades',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-gray-200">
                  <img src={tick} alt="" className="w-4 h-4 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Core Values */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {values.map((val, i) => (
            <motion.div
              key={i}
              className="group bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 text-center hover:bg-white/10 transition-all duration-300 shadow-xl overflow-hidden relative"
              initial="hidden"
              whileInView="visible"
              variants={fadeUp}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-md"
                style={{ backgroundColor: `${val.color}25`, color: val.color }}
              >
                {val.icon}
              </div>
              <h4 className="text-lg font-bold mb-3 group-hover:text-red-400 transition-colors uppercase tracking-wide">
                {val.title}
              </h4>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{val.description}</p>
              <div
                className="absolute bottom-0 left-0 w-full h-1 rounded-b-2xl transition-all duration-300 group-hover:h-1.5"
                style={{ backgroundColor: val.color }}
              />
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── SECTION 5: CTA ── */}
      <section className="relative px-4 md:px-12 py-20 text-white overflow-hidden" id="about-cta">
        <div className="absolute w-96 h-96 bg-red-600/25 blur-[160px] rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10" />

        <motion.div
          className="max-w-4xl mx-auto bg-gradient-to-r from-gray-900/95 via-[#181a20] to-gray-900/95 backdrop-blur-2xl p-8 sm:p-14 rounded-3xl border border-white/10 shadow-2xl text-center relative overflow-hidden"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-red-600 to-transparent" />

          <span className="inline-block bg-red-600/20 text-red-500 border border-red-600/30 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
            Start Your Journey
          </span>
          <h2 className="text-3xl sm:text-5xl font-black uppercase leading-tight tracking-wide mb-4">
            Ready to{' '}
            <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">Transform</span>
            <br />
            Your Body?
          </h2>
          <p className="text-gray-300 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto mb-8">
            Join over 900+ members who have already started their transformation journey at IDOL FITNESS ZONE.
            Visit any of our 3 branches in Chettipalayam JJ Nagar, Pappampatti Pirivu, or Peedampalli.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-red-600 hover:bg-red-500 text-white font-bold px-8 py-4 rounded-xl transition-all shadow-lg shadow-red-600/30 flex items-center justify-center gap-3 text-xs uppercase tracking-wider group"
            >
              <span>Start Your Fitness Journey</span>
              <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="bg-transparent border-2 border-red-600/50 hover:bg-red-600/10 text-white font-bold px-8 py-4 rounded-xl transition-all flex items-center justify-center gap-3 text-xs uppercase tracking-wider"
            >
              Book a Fitness Consultation
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default AboutUs;
